import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';
import { ProfilePhoto } from './ProfilePhoto';
import BottomNav from './BottomNav';
import { LogOut, Settings, Coins } from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!isAuthenticated) {
    return children;
  }

  const isLoginOrRegister = location.pathname === '/login' || location.pathname === '/register';
  if (isLoginOrRegister) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/80">
      {/* Fixed Top Navigation Bar */}
      <nav className="fixed top-0 right-0 left-0 z-50 glass border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo / Home */}
            <button 
              onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/dashboard')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="nav-logo"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 coin-gradient rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm shadow-amber-500/20">
                <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-slate-900 hidden sm:block">CampusCoins</span>
            </button>

            {/* Right Side Actions */}
            <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
              {/* User greeting - hidden on mobile */}
              <span className="text-sm text-slate-600 hidden md:block">
                Hola, <span className="font-medium text-slate-900">{user?.name?.split(' ')[0]}</span>
              </span>
              
              {/* Profile Photo */}
              <div className="hidden sm:flex items-center">
                <ProfilePhoto 
                  photoUrl={user?.photo_url} 
                  name={user?.name} 
                  size={32} 
                />
              </div>
              
              {/* Notifications */}
              <NotificationBell user={user} />
              
              {/* Settings */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
                className="h-8 w-8 sm:h-9 sm:w-9 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                title="Configuración"
                data-testid="nav-settings-btn"
              >
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              
              {/* Logout */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="h-8 w-8 sm:h-9 sm:w-9 text-slate-600 hover:text-red-600 hover:bg-red-50"
                title="Cerrar sesión"
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Main Content with top padding for fixed nav and bottom padding for mobile nav */}
      <main className="pt-14 pb-16 sm:pb-0 px-3 sm:px-0">
        {children}
      </main>
      
      {/* Mobile Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
